/** El historial de una página: versiones anteriores, para verlas y volver a ellas. */
import { useEffect, useState } from 'react'

import { ErrorDeApi, api } from '../api'
import { Dialogo, Vacio } from '../componentes/piezas'
import { usePortal } from '../contexto'
import { fechaYHora } from '../textos'
import type { Bloque, Pagina, Version, VersionCompleta } from './tipos'

/** El texto de unos bloques, con sus saltos, para poder enseñar cómo era una versión. */
export function textoDeBloques(bloques: Bloque[] | null | undefined, nivel = 0): string {
  if (!bloques) return ''
  return bloques.map((b) => {
    const contenido = b.content
    let linea = ''
    if (typeof contenido === 'string') linea = contenido
    else if (Array.isArray(contenido)) {
      linea = contenido.map((t) => (typeof t === 'string' ? t : (t as { text?: string }).text ?? '')).join('')
    }
    const marca = b.type === 'bulletListItem' ? '• ' : b.type === 'checkListItem' ? '☐ '
      : b.type === 'numberedListItem' ? '1. ' : b.type === 'heading' ? '# ' : ''
    const hijos = textoDeBloques(b.children as Bloque[] | undefined, nivel + 1)
    return `${'  '.repeat(nivel)}${marca}${linea}${hijos ? `\n${hijos}` : ''}`
  }).join('\n')
}

export default function Historial({ pagina, rutaPaginas, alRestaurar, alCerrar }: {
  pagina: Pagina
  rutaPaginas: string
  alRestaurar: (p: Pagina) => void
  alCerrar: () => void
}) {
  const { avisar } = usePortal()
  const [lista, setLista] = useState<Version[] | null>(null)
  const [elegida, setElegida] = useState<VersionCompleta | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const base = `${rutaPaginas}/${pagina.id}/versiones`

  useEffect(() => {
    api.get<Version[]>(base).then(setLista).catch(() => setLista([]))
  }, [base])

  const abrir = async (id: number) => {
    try {
      setElegida(await api.get<VersionCompleta>(`${base}/${id}`))
    } catch (e) {
      avisar(e instanceof ErrorDeApi ? e.message : 'No se ha podido abrir la versión.', 'malo')
    }
  }

  const restaurar = async () => {
    if (!elegida) return
    setOcupado(true)
    try {
      alRestaurar(await api.post<Pagina>(`${base}/${elegida.id}/restaurar`))
    } catch (e) {
      avisar(e instanceof ErrorDeApi ? e.message : 'No se ha podido restaurar.', 'malo')
      setOcupado(false)
    }
  }

  return (
    <Dialogo titulo="Historial de versiones" alCerrar={alCerrar}>
      {lista === null && <p className="cargando">Cargando…</p>}
      {lista?.length === 0 && (
        <Vacio titulo="Todavía no hay versiones anteriores">
          <p>Se guarda una foto de la página cuando se cambia su texto, como mucho cada diez minutos.</p>
        </Vacio>
      )}
      {lista && lista.length > 0 && (
        <div className="historial-bd">
          <ul className="historial-bd__lista">
            {lista.map((v) => (
              <li key={v.id}>
                <button className={`historial-bd__version${elegida?.id === v.id ? ' historial-bd__version--activa' : ''}`}
                        onClick={() => void abrir(v.id)}>
                  <strong>{fechaYHora(v.created_at)}</strong>
                  <span>{v.autor?.nombre ?? 'Alguien'} · {v.title || 'Sin título'}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="historial-bd__vista">
            {elegida ? (
              <>
                <h3>{elegida.title || 'Sin título'}</h3>
                <pre className="historial-bd__texto">{textoDeBloques(elegida.content) || '(página vacía)'}</pre>
                {pagina.puede_editar && (
                  <button className="boton boton--principal" disabled={ocupado} onClick={() => void restaurar()}>
                    Restaurar esta versión
                  </button>
                )}
                <p className="campo__ayuda">
                  Al restaurar, la versión actual se guarda antes: siempre se puede volver.
                </p>
              </>
            ) : (
              <p className="campo__ayuda">Elige una versión para ver cómo era.</p>
            )}
          </div>
        </div>
      )}
    </Dialogo>
  )
}
